import React, {useMemo} from 'react';
import * as THREE from 'three';
import {
    AdvancedShelf,
    AdvancedLowerShelf,
    AdvancedUpperShelf,
    PreviewLocation,
    AdvancedMiddleShelf,
    PreviewFormValues,
} from 'components/customer/Preview3D/types';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import useMaterialTexture from 'components/customer/Preview3D/lib/useMaterialTexture';
import {useAppSelector} from 'store/customer';
import {shallowEqual} from 'react-redux';
import {getMaterial} from 'components/customer/Materials/store/selectors/materialSelector';
import {MaterialType} from 'components/customer/Materials/entity';
import {getShelfStyleLabel} from 'components/customer/Preview3D/helpers';
import {FontLoader} from 'three/examples/jsm/loaders/FontLoader.js';
import {TextGeometry} from 'three/examples/jsm/geometries/TextGeometry';
import {
    DEFAULT_VENT_FRONT_WIDTH,
    DEFAULT_VENT_WIDTH,
} from 'components/customer/Preview3D/values/useRangehoodDynamicValues';
import {use3DContext} from 'components/customer/Preview3D/components/Preview3DComponent';
import {CabinetTopEnum} from 'shared/CabinetTop';
import {generateKeyShapeMesh} from 'components/customer/Preview3DCommon/shapes/keyShape';
import {generateArrowShapeMesh} from 'components/customer/Preview3DCommon/shapes/arrowShape';
import {getShelfThickness} from 'components/customer/Preview3DCommon/shapes/advancedShelf';
import {
    generateDrawers,
    getDrawerFormValues,
} from 'components/customer/Preview3DCommon/values/drawer';
import {generateUShapeMesh} from 'components/customer/Preview3DCommon/shapes/uShape';
import {applyQuarterCircleArcShape} from 'components/customer/Preview3DCommon/shapes/shapeUtils';
import {rotateAroundCenter} from 'components/customer/RoomPlanner/helpers/rotateAroundCenter';
import {
    applyCarcaseMaterial,
    buildLShapedReturnShelves,
    buildClipPanel,
    buildCornerShapedPanel,
    buildRadiusPanel,
    buildShelvesWithOvenVents,
    buildShelvesWithRangehoodCutout,
    buildLShapedPanelForPantry,
    buildUShapedPanelShelves,
    buildUpperAndLowerLeftRightUShapeShelves,
    buildUpperAndLowerUShapeShelves,
    buildCornerShapePanelForPantry,
    buildCornerShapePanelFor3PartPantry,
    buildLShapedPanelForPantryReturn,
    buildBottomPanelWithVent,
    buildTopBottomLShapedPanels,
    buildLShapedPanelShelves,
    buildSingleLShapePanel,
    createLineSegments,
    resolveShapedShelfCount,
    getYawedLabelStart,
} from 'components/customer/Preview3DCommon/shelves/common';
const useShapedPanel = (
    scene: React.MutableRefObject<THREE.Scene>,
    showTexture?: boolean,
    previewLocation?: PreviewLocation
) => {
    const {values, productDataStore} = use3DContext();
    const isFfSolidTop = values.cabinet_top == CabinetTopEnum.FF_SOLID_TOP;
    const isFfRailOnFlat = values.cabinet_top == CabinetTopEnum.FF_RAIL_ON_FLAT;
    const horizontalGrainCarc = Boolean(values.hor_grain_carc);
    const horizontalGrainExt = Boolean(values.hor_grain_ext);

    const variables: TemplateVariable = JSON.parse(
        productDataStore.current?.template_3d[0].attributes?.variables
    ) as TemplateVariable;
    const isLeftReturn = variables?.isLeftReturn;
    const isLsSolidTop =
        values.cabinet_top == CabinetTopEnum.LS_SOLID_TOP ||
        !!variables?.isLsSolidTop;

    const {
        carcaseTexture,
        carcaseEdgeTexture,
        carcaseThickness: carcaseThicknessFromMaterial,
        carcaseSubstrateMaterial,
        exteriorTexture,
        exteriorEdgeTexture,
        exteriorThickness,
    } = useMaterialTexture();
    // The cabinet's own panels (sides, bottom, top, partitions) are cut from
    // the carcase material; only the shelves follow the shelf material's
    // thickness.
    const carcaseThickness = carcaseThicknessFromMaterial;
    const shelfThickness = values
        ? getShelfThickness(values)
        : carcaseThicknessFromMaterial;
    const selectedCarcaseMaterial = useAppSelector(
        (state) => getMaterial(state, MaterialType.CARCASE),
        shallowEqual
    );
    const nonSupplyCarcase = selectedCarcaseMaterial?.brand_id === 23;
    const defaultWidthOffset = useMemo(
        () => carcaseThickness * 3,
        [carcaseThickness]
    );

    const generateRectMesh = ({
        width,
        depth,
        carcaseTexture,
        carcaseEdgeTexture,
    }: {
        width: number;
        depth: number;
        carcaseTexture: THREE.Texture;
        carcaseEdgeTexture: THREE.Texture;
    }) => {
        const shape = new THREE.Shape();

        // Start at the bottom-left corner
        shape.moveTo(0, 0);

        // Draw the bottom line
        shape.lineTo(width, 0);

        // Draw the right line
        shape.lineTo(width, depth);

        // Draw the top line
        shape.lineTo(0, depth);

        // Draw the left line to close the shape
        shape.lineTo(0, 0);

        // Extrude settings for the 3D geometry
        const extrudeSettings = {
            steps: 1,
            depth: carcaseThickness,
            bevelEnabled: false,
        };

        // Create the extruded geometry
        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

        const materials = applyCarcaseMaterial({
            geometry,
            texture: carcaseTexture,
            edgeTexture: carcaseEdgeTexture,
            hasHorizontalGrain: horizontalGrainCarc,
            showTexture,
            nonSupplyCarcase,
            carcaseSubstrateMaterial,
        });

        // Create and return the mesh
        return new THREE.Mesh(geometry, materials);
    };

    const generateLShapeMesh = ({
        leftWidth,
        leftDepth,
        rightDepth,
        radius,
        rightWidth,
        carcaseTexture,
        carcaseEdgeTexture,
        carcaseThickness,
        values,
    }: {
        leftWidth: number;
        leftDepth: number;
        rightDepth: number;
        rightWidth: number;
        radius: number;
        carcaseTexture: THREE.Texture;
        carcaseEdgeTexture: THREE.Texture;
        carcaseThickness: number;
        values: PreviewFormValues;
    }) => {
        // Create the shape
        const shape = new THREE.Shape();

        // Clamp radius to fit within both horizontal and vertical constraints
        const maxRadiusHorizontal = rightWidth - leftDepth;
        const maxRadiusVertical = leftWidth - rightDepth;
        const clampedRadius = Math.min(
            radius,
            maxRadiusHorizontal,
            maxRadiusVertical
        );

        // Start at the bottom-left corner
        shape.moveTo(0, 0);

        // Draw the left vertical line
        shape.lineTo(0, leftWidth);

        // Draw the top horizontal line to the right (end of left width)
        shape.lineTo(leftDepth, leftWidth);

        // Draw the vertical line downwards to the radius start point
        shape.lineTo(leftDepth, rightDepth + clampedRadius);

        // Create the rounded corner with absarc
        shape.absarc(
            leftDepth + clampedRadius, // x coordinate of arc's center
            rightDepth + clampedRadius, // y coordinate of arc's center
            clampedRadius, // radius of the arc
            Math.PI, // start angle (180 degrees in radians)
            Math.PI * 1.5, // end angle (270 degrees in radians)
            false // clockwise direction
        );

        // Draw the remaining horizontal line to the right
        shape.lineTo(rightWidth, rightDepth);

        // Draw the right vertical line upwards
        shape.lineTo(rightWidth, 0);

        // Close the shape by drawing the bottom horizontal line to the left
        shape.lineTo(0, 0);

        const extrudeSettings = {
            steps: 1,
            depth: carcaseThickness,
            bevelEnabled: false,
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

        const materials = applyCarcaseMaterial({
            geometry,
            texture: carcaseTexture,
            edgeTexture: carcaseEdgeTexture,
            useSubstrateEdge: false,
            flipGrain: false,
            hasHorizontalGrain: Boolean(values.hor_grain_carc),
            showTexture,
            nonSupplyCarcase,
            carcaseSubstrateMaterial,
        });

        // Create the mesh
        return new THREE.Mesh(geometry, materials);
    };

    const createLineSegmentsForCarcase = <
        G extends THREE.BufferGeometry,
        M extends THREE.Material | THREE.Material[],
    >(
        mesh: THREE.Mesh<G, M>
    ) =>
        createLineSegments(mesh as unknown as THREE.Mesh, {
            nonSupplyCarcase,
            showTexture,
        });

    const getCornerShapeOffsets = () => {
        let widthOffset = 0;

        switch (Number(carcaseThickness)) {
            case 12.5:
            case 12:
            case 25.5:
                widthOffset = carcaseThickness * 3 + 1;
                break;
            default:
                widthOffset = defaultWidthOffset;
                break;
        }

        return [widthOffset];
    };

    const getCornerLShapeOffsets = () => {
        let widthOffset = 0;

        switch (Number(carcaseThickness)) {
            case 16.5:
                widthOffset = defaultWidthOffset;
                break;
            case 25.5:
                widthOffset = carcaseThickness * 2;
                break;
            case 12.5:
            case 12:
                widthOffset = carcaseThickness * 4;
                break;
            default:
                widthOffset = carcaseThickness * 1.5;
                break;
        }

        return [widthOffset];
    };

    const addLabelMesh = (
        shelfLabel: string,
        thickness: number,
        parentMesh: THREE.Mesh,
        positionY: number,
        positionXOffset = 0,
        rotationY = 0,
        centerX?: number
    ) => {
        if (previewLocation !== 'SHELVES') return;

        const fontLoader = new FontLoader();

        fontLoader.load(
            '/templates/3D/fonts/Poppins SemiBold_Regular.json',
            (font) => {
                const textGeometry = new TextGeometry(shelfLabel, {
                    font,
                    size: 4,
                    depth: 0.5,
                });

                const textMesh = new THREE.Mesh(
                    textGeometry,
                    new THREE.MeshStandardMaterial({
                        color: '#333',
                    })
                );

                const scale = 7;
                textMesh.scale.set(scale, scale, scale);

                const boundingBox = new THREE.Box3().setFromObject(textMesh);
                const meshWidth = boundingBox.max.x - boundingBox.min.x;

                textMesh.rotation.x = (90 * Math.PI) / 180;
                if (rotationY) {
                    textMesh.rotation.y = rotationY;
                }

                const box = new THREE.Box3().setFromObject(parentMesh);
                const meshXParent = box.max.x - box.min.x;

                const isRightReturnProduct =
                    !variables?.isLeftReturn && variables.isReturnProduct;

                const aimed =
                    typeof centerX === 'number'
                        ? getYawedLabelStart(
                              centerX,
                              positionY,
                              meshWidth,
                              rotationY
                          )
                        : null;

                let positionX =
                    aimed?.x ??
                    meshXParent / 2 - meshWidth / 2 - positionXOffset;

                if (isRightReturnProduct) {
                    textMesh.scale.x = -textMesh.scale.x;
                }

                if (isRightReturnProduct) {
                    positionX = meshXParent - 10;
                }

                if (isLeftReturn) {
                    positionX = meshXParent - meshWidth - 10;
                }

                textMesh.position.set(
                    positionX,
                    aimed?.y ?? positionY,
                    thickness + 5
                );
                textMesh.name = shelfLabel;

                parentMesh.add(textMesh);
            }
        );
    };

    const renderLShapedPanel = () => {
        const height = Number(values.cabinet_height);
        // Apply the same L-shape shelf tweaks the RoomPlanner uses so the
        // shelves line up with the carcass (matches advancedShelf.ts).
        const isUpperProduct = Boolean(variables?.isUpperProduct);
        const radius = 20;

        const rightWidth =
            values.cabinet_right_width - carcaseThickness - carcaseThickness;
        const leftWidth =
            Number(values.cabinet_left_width) +
            2 +
            (isUpperProduct
                ? -(carcaseThickness * 2) - 2
                : -(carcaseThickness * 2));

        const shelvesCount = Number(values.simple_shelves_amount);
        const advancedShelves = values.shelves as unknown as AdvancedShelf[];
        const isUpper3DoorProduct =
            variables?.is3DoorProduct && variables?.isUpperProduct;
        return buildLShapedPanelShelves({
            values,
            variables,
            shelvesCount,
            shelves: advancedShelves,
            carcaseThickness,
            shelfThickness,
            height,
            leftWidth,
            rightWidth,
            radius,
            isLsSolidTop,
            isOpenLShape: Boolean(variables?.isOpenLShape),
            isUpper3DoorProduct: Boolean(isUpper3DoorProduct),
            makeLShapeMesh: ({leftDepth, rightDepth, thickness}) =>
                generateLShapeMesh({
                    leftWidth,
                    leftDepth,
                    rightDepth,
                    radius,
                    rightWidth,
                    carcaseTexture,
                    carcaseEdgeTexture,
                    carcaseThickness: thickness,
                    values,
                }),
            lineSegmentsForMesh: (mesh) => createLineSegmentsForCarcase(mesh),
            onLabel: (mesh, ctx) => {
                const shelfLabel = `Shelf ${ctx.index} - ${getShelfStyleLabel(
                    values,
                    ctx.shelfStyle
                )}`;
                addLabelMesh(
                    shelfLabel,
                    ctx.panelThickness ?? carcaseThickness,
                    mesh,
                    ctx.suggestedLabelY ?? 0,
                    ctx.suggestedLabelXOffset ?? 0
                );
            },
        });
    };

    const renderUShapedPanel = () => {
        const advancedShelves = values.shelves as unknown as AdvancedShelf[];
        return buildUShapedPanelShelves({
            values,
            variables,
            shelves: advancedShelves,
            carcaseThickness,
            shelfThickness,
            makeUShapeMesh: ({
                width,
                depth,
                cutoutWidth,
                cutoutHeight,
                radius,
            }) =>
                generateUShapeMesh({
                    width,
                    depth,
                    cutoutWidth,
                    cutoutHeight,
                    radius,
                    carcaseTexture,
                    carcaseEdgeTexture,
                    carcaseThickness: shelfThickness,
                    horizontalGrainCarc,
                    showTexture,
                }),
            lineSegmentsForMesh: (mesh) => createLineSegmentsForCarcase(mesh),
            onLabel: (mesh, ctx) => {
                const shelfLabel = `Shelf ${ctx.index} - ${getShelfStyleLabel(
                    values,
                    ctx.shelfStyle
                )}`;
                addLabelMesh(
                    shelfLabel,
                    ctx.panelThickness ?? carcaseThickness,
                    mesh,
                    ctx.suggestedLabelY ?? 0
                );
            },
        });
    };

    const getLShapeOffsetsForReturn = () => {
        let widthOffset = 0;
        let positionXOffset = 0;
        let positionXOffsetRightReturn = 0;

        switch (Number(carcaseThickness)) {
            case 16.5:
                widthOffset = carcaseThickness * 4;
                positionXOffset = carcaseThickness + 5;
                positionXOffsetRightReturn =
                    carcaseThickness + carcaseThickness / 2;
                break;
            case 25.5:
                widthOffset = carcaseThickness * 3;
                positionXOffset = carcaseThickness;
                positionXOffsetRightReturn =
                    carcaseThickness + carcaseThickness / 2;
                break;
            case 12.5:
            case 12:
                widthOffset = carcaseThickness * 5;
                positionXOffset = carcaseThickness * 2;
                positionXOffsetRightReturn =
                    carcaseThickness + carcaseThickness / 2;
                break;
            default:
                widthOffset = carcaseThickness * 2.5;
                positionXOffset = carcaseThickness * 0.5;
                positionXOffsetRightReturn = carcaseThickness;
                break;
        }

        return [widthOffset, positionXOffset, positionXOffsetRightReturn];
    };

    const renderLShapedPanelForReturn = () => {
        const advancedShelves = values.shelves as unknown as AdvancedShelf[];
        const shelvesCount = advancedShelves?.length;

        const includeFaces =
            typeof values.include_drawer_faces !== 'undefined'
                ? Boolean(values.include_drawer_faces)
                : true;
        const voidWidth =
            values.cabinet_void_width &&
            Boolean(values.cabinet_cover_void) &&
            includeFaces
                ? Number(values.cabinet_void_width)
                : 0;
        const returnPanelWidth = values.cabinet_return_panel_width
            ? parseFloat(values.cabinet_return_panel_width?.toString())
            : 0;

        const [widthOffset] = getLShapeOffsetsForReturn();

        return buildLShapedReturnShelves({
            values,
            variables,
            shelves: advancedShelves,
            shelvesCount,
            carcaseThickness,
            shelfThickness,
            widthOffset,
            voidWidth,
            returnPanelWidth,
            isLeftReturn,
            makeLShapeMesh: ({
                leftWidth,
                leftDepth,
                rightDepth,
                radius,
                rightWidth,
            }) =>
                generateLShapeMesh({
                    leftWidth,
                    leftDepth,
                    rightDepth,
                    radius,
                    rightWidth,
                    carcaseTexture,
                    carcaseEdgeTexture,
                    carcaseThickness: shelfThickness,
                    values,
                }),
            lineSegmentsForMesh: (mesh) => createLineSegmentsForCarcase(mesh),
            onLabel: (mesh, ctx) => {
                const shelfLabel = `Shelf ${ctx.index} - ${getShelfStyleLabel(
                    values,
                    ctx.shelfStyle
                )}`;
                addLabelMesh(
                    shelfLabel,
                    ctx.panelThickness ?? carcaseThickness,
                    mesh,
                    ctx.suggestedLabelY ?? 0
                );
            },
        });
    };

    /**
     * Creates an L-shaped partition with specified dimensions and textures.
     * @param {Object} params - The parameters for the partition.
     * @param {number} params.leftWidth - The width of the left section.
     * @param {number} params.leftDepth - The depth of the left section.
     * @param {number} params.rightDepth - The depth of the right section.
     * @param {number} params.radius - The radius of the corner.
     * @param {number} params.rightWidth - The width of the right section.
     * @param {THREE.Texture} params.carcaseTexture - The texture for the main carcase.
     * @param {THREE.Texture} params.carcaseEdgeTexture - The texture for the edges.
     * @param {number} params.heightOffset - The height offset for the partition.
     * @param {string} params.style - The style of the partition.
     * @param {number} params.index - The index identifier for the partition.
     * @return {THREE.Mesh<THREE.ExtrudeGeometry, (THREE.MeshStandardMaterial | THREE.MeshPhongMaterial)[], THREE.Object3DEventMap>} - Indicates whether the partition was successfully created.
     */
    function createLShapePartition({
        leftWidth,
        leftDepth,
        rightDepth,
        radius,
        rightWidth,
        carcaseTexture,
        carcaseEdgeTexture,
        heightOffset,
        usePlain = false,
    }: {
        leftWidth: number;
        leftDepth: number;
        rightDepth: number;
        radius: number;
        rightWidth: number;
        carcaseTexture: THREE.Texture;
        carcaseEdgeTexture: THREE.Texture;
        style: string;
        index: number;
        heightOffset: number;
        usePlain: boolean;
    }) {
        // create object to render

        const partitionMesh = usePlain
            ? generateRectMesh({
                  width: values.cabinet_width - carcaseThickness,
                  depth: values.cabinet_depth - carcaseThickness,
                  carcaseTexture,
                  carcaseEdgeTexture,
              })
            : generateLShapeMesh({
                  leftWidth: leftWidth - 3.5,
                  leftDepth,
                  rightDepth,
                  radius,
                  rightWidth,
                  carcaseTexture,
                  carcaseEdgeTexture,
                  carcaseThickness,
                  values,
              });

        let positionX = carcaseThickness;

        // determines if the partition should face right or left
        if (!isLeftReturn) {
            // Mirror about the inner right wall to keep the partition inside the
            // carcase; rightWidth + widthOffset pushed it past the wall.
            positionX = Number(values.cabinet_width);
            partitionMesh.scale.x = -1;
        }

        partitionMesh.scale.y = -1;

        // assign position
        partitionMesh.position.set(positionX, leftWidth, heightOffset);

        return partitionMesh;
    }

    const renderLShapedPanelForPantryReturn = () => {
        const includeFaces =
            typeof values.include_drawer_faces !== 'undefined'
                ? Boolean(values.include_drawer_faces)
                : true;
        const voidWidth =
            values.cabinet_void_width &&
            Boolean(values.cabinet_cover_void) &&
            includeFaces
                ? Number(values.cabinet_void_width)
                : 0;
        const returnPanelWidth = values.cabinet_return_panel_width
            ? parseFloat(values.cabinet_return_panel_width?.toString())
            : 0;

        const upperShelvesCount = values?.upper_shelves
            ? (values?.upper_shelves as unknown as Array<number>)?.length
            : 0;
        const lowerShelvesCount = values?.lower_shelves
            ? (values?.lower_shelves as unknown as Array<number>)?.length
            : 0;

        const upperShelves =
            values.upper_shelves as unknown as AdvancedUpperShelf[];
        const lowerShelves =
            values.lower_shelves as unknown as AdvancedLowerShelf[];
        const shelvesHeightReference = {
            upper_shelves:
                parseFloat(values.cabinet_height?.toString()) -
                values.cabinet_partition_height,
            lower_shelves: values.cabinet_partition_height,
        };

        const upperShelfOffset = shelvesHeightReference.lower_shelves;
        const height = Number(values.cabinet_height) - upperShelfOffset;

        const [widthOffset] = getLShapeOffsetsForReturn();
        return buildLShapedPanelForPantryReturn({
            values,
            variables,
            carcaseThickness,
            shelfThickness,
            voidWidth,
            returnPanelWidth,
            upperShelves,
            lowerShelves,
            upperShelvesCount,
            lowerShelvesCount,
            upperShelfOffset,
            heightUpperSection: height,
            isLeftReturn: Boolean(variables?.isLeftReturn),
            widthOffset,
            makeShelfMesh: ({
                leftWidth,
                leftDepth,
                rightDepth,
                rightWidth,
                radius,
            }) =>
                generateLShapeMesh({
                    leftWidth: leftWidth - 3.5,
                    leftDepth,
                    rightDepth,
                    radius,
                    rightWidth,
                    carcaseTexture,
                    carcaseEdgeTexture,
                    carcaseThickness: shelfThickness,
                    values,
                }),
            makePartitionMesh: ({
                leftWidth,
                leftDepth,
                rightDepth,
                rightWidth,
                radius,
                heightOffset,
                usePlain,
            }) =>
                createLShapePartition({
                    leftWidth,
                    leftDepth,
                    rightDepth,
                    radius,
                    rightWidth,
                    carcaseTexture,
                    carcaseEdgeTexture,
                    style: '',
                    index: 0,
                    heightOffset,
                    usePlain,
                }),
            lineSegmentsForMesh: (mesh) => createLineSegmentsForCarcase(mesh),
            onLabel: (mesh, ctx) => {
                if (ctx.label === 'Horizontal Partition') {
                    addLabelMesh(
                        'Horizontal Partition',
                        ctx.panelThickness ?? carcaseThickness,
                        mesh,
                        ctx.suggestedLabelY ?? 0
                    );
                    return;
                }
                addLabelMesh(
                    `${ctx.label} - ${getShelfStyleLabel(values, ctx.shelfStyle)}`,
                    ctx.panelThickness ?? carcaseThickness,
                    mesh,
                    ctx.suggestedLabelY ?? 0
                );
            },
        });
    };

    const renderUpperAndLowerUShapeShelves = () => {
        const upperShelvesCount = values?.upper_shelves
            ? (values?.upper_shelves as unknown as Array<number>)?.length
            : 0;
        const lowerShelvesCount = values?.lower_shelves
            ? (values?.lower_shelves as unknown as Array<number>)?.length
            : 0;

        const upperShelves =
            values.upper_shelves as unknown as AdvancedUpperShelf[];
        const lowerShelves =
            values.lower_shelves as unknown as AdvancedLowerShelf[];
        if (!upperShelvesCount && !lowerShelvesCount) return [];

        return buildUpperAndLowerUShapeShelves({
            values,
            upperShelves,
            lowerShelves,
            carcaseThickness,
            shelfThickness,
            makeUShapeMesh: ({
                width,
                depth,
                cutoutWidth,
                cutoutHeight,
                radius,
            }) =>
                generateUShapeMesh({
                    width,
                    depth,
                    cutoutWidth,
                    cutoutHeight,
                    radius,
                    carcaseTexture,
                    carcaseEdgeTexture,
                    carcaseThickness: shelfThickness,
                    horizontalGrainCarc,
                    showTexture,
                }),
            lineSegmentsForMesh: (mesh) => createLineSegmentsForCarcase(mesh),
            onLabel: (mesh, ctx) => {
                addLabelMesh(
                    `${ctx.label} - ${getShelfStyleLabel(values, ctx.shelfStyle)}`,
                    ctx.panelThickness ?? carcaseThickness,
                    mesh,
                    ctx.suggestedLabelY ?? 0
                );
            },
        });
    };

    const renderUpperAndLowerLeftRightUShapedShelves = () => {
        const upperShelvesCount = values?.upper_shelves
            ? (values?.upper_shelves as unknown as Array<number>)?.length
            : 0;
        const lowerLeftShelvesCount = values?.lower_shelves
            ? (values?.lower_shelves as unknown as Array<number>)?.length
            : 0;
        const lowerRightShelvesCount = values?.shelves
            ? (values?.shelves as unknown as Array<number>)?.length
            : 0;

        const upperShelves =
            values.upper_shelves as unknown as AdvancedUpperShelf[];
        const lowerLeftShelves =
            values.lower_shelves as unknown as AdvancedLowerShelf[];
        const lowerRightShelves = values.shelves as unknown as AdvancedShelf[];

        const partitionWidth = values.cabinet_partition_width
            ? values.cabinet_partition_width < 1
                ? parseFloat(values.cabinet_width?.toString()) *
                  values.cabinet_partition_width
                : parseFloat(values.cabinet_partition_width?.toString())
            : 0;

        if (
            !upperShelvesCount &&
            !lowerLeftShelvesCount &&
            !lowerRightShelvesCount
        )
            return [];

        return buildUpperAndLowerLeftRightUShapeShelves({
            values,
            carcaseThickness,
            shelfThickness,
            upperShelves,
            lowerLeftShelves,
            lowerRightShelves,
            partitionWidth,
            makeUShapeMesh: ({
                width,
                depth,
                cutoutWidth,
                cutoutHeight,
                radius,
            }) =>
                generateUShapeMesh({
                    width,
                    depth,
                    cutoutWidth,
                    cutoutHeight,
                    radius,
                    carcaseTexture,
                    carcaseEdgeTexture,
                    carcaseThickness: shelfThickness,
                    horizontalGrainCarc,
                    showTexture,
                }),
            lineSegmentsForMesh: (mesh) => createLineSegmentsForCarcase(mesh),
            onLabel: (mesh, ctx) => {
                addLabelMesh(
                    `${ctx.label} - ${getShelfStyleLabel(values, ctx.shelfStyle)}`,
                    ctx.panelThickness ?? carcaseThickness,
                    mesh,
                    ctx.suggestedLabelY ?? 0
                );
            },
        });
    };

    const renderSingleLShapePanel = () => {
        const mesh = buildSingleLShapePanel({
            values,
            carcaseThickness,
            centerGeometry: true,
            makeLShapeMesh: ({
                leftWidth,
                leftDepth,
                rightDepth,
                radius,
                rightWidth,
            }) =>
                generateLShapeMesh({
                    leftWidth,
                    leftDepth,
                    rightDepth,
                    radius,
                    rightWidth,
                    carcaseTexture,
                    carcaseEdgeTexture,
                    carcaseThickness,
                    values,
                }),
            lineSegmentsForMesh: (mesh) => createLineSegmentsForCarcase(mesh),
            postProcess: (mesh) => rotateAroundCenter(mesh, Math.PI),
        });

        scene.current.add(mesh);
    };

    const generateShelfWithOvenVentMesh = ({
        width,
        depth,
        carcaseTexture,
        carcaseEdgeTexture,
        thickness = carcaseThickness,
    }: {
        width: number;
        depth: number;
        carcaseTexture: THREE.Texture;
        carcaseEdgeTexture: THREE.Texture;
        thickness?: number;
    }) => {
        const shape = new THREE.Shape();

        // Panel dimensions

        // Hole dimensions
        const holeWidth = (width - 300) / 2;
        const holeHeight = depth - 80;
        const gap = 100;

        // Hole positions
        const hole1X = 100;
        const hole2X = hole1X + holeWidth + gap;
        const holeY = 45;

        shape.moveTo(0, 0);
        shape.lineTo(width, 0);
        shape.lineTo(width, depth);
        shape.lineTo(0, depth);
        shape.lineTo(0, 0);

        // Create the first hole (a rectangle with dynamic position and size)
        const hole1 = new THREE.Path();
        hole1.moveTo(hole1X, holeY);
        hole1.lineTo(hole1X + holeWidth, holeY);
        hole1.lineTo(hole1X + holeWidth, holeY + holeHeight);
        hole1.lineTo(hole1X, holeY + holeHeight);
        hole1.lineTo(hole1X, holeY);
        shape.holes.push(hole1);

        // Create the second hole (a rectangle with dynamic position and size)
        const hole2 = new THREE.Path();
        hole2.moveTo(hole2X, holeY);
        hole2.lineTo(hole2X + holeWidth, holeY);
        hole2.lineTo(hole2X + holeWidth, holeY + holeHeight);
        hole2.lineTo(hole2X, holeY + holeHeight);
        hole2.lineTo(hole2X, holeY);
        shape.holes.push(hole2);

        const extrudeSettings = {
            steps: 1,
            depth: thickness,
            bevelEnabled: false,
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        const materials = applyCarcaseMaterial({
            geometry,
            texture: carcaseTexture,
            edgeTexture: carcaseEdgeTexture,
            useSubstrateEdge: true,
            flipGrain: true,
            hasHorizontalGrain: horizontalGrainCarc,
            showTexture,
            nonSupplyCarcase,
            carcaseSubstrateMaterial,
        });

        const position = geometry.attributes.position;
        const faceCount = position.count / 3;

        for (let i = 0; i < faceCount; i++) {
            const vertex1 = new THREE.Vector3().fromBufferAttribute(
                position,
                i * 3
            );
            const vertex2 = new THREE.Vector3().fromBufferAttribute(
                position,
                i * 3 + 1
            );
            const vertex3 = new THREE.Vector3().fromBufferAttribute(
                position,
                i * 3 + 2
            );

            if (vertex1.z === 0 && vertex2.z === 0 && vertex3.z === 0) {
                geometry.addGroup(i * 3, 3, 0); // Front face
            } else if (
                vertex1.z === extrudeSettings.depth &&
                vertex2.z === extrudeSettings.depth &&
                vertex3.z === extrudeSettings.depth
            ) {
                geometry.addGroup(i * 3, 3, 1); // Back face
            } else {
                geometry.addGroup(
                    i * 3,
                    3,
                    [33, 32].includes(geometry.groups?.length) ? 2 : 1
                ); // Sides
            }
        }

        return new THREE.Mesh(geometry, materials);
    };

    const renderShelvesWithOvenVents = () => {
        return buildShelvesWithOvenVents({
            values,
            variables,
            carcaseThickness,
            shelfThickness,
            makeVentShelfMesh: ({width, depth, thickness}) =>
                generateShelfWithOvenVentMesh({
                    width,
                    depth,
                    carcaseTexture,
                    carcaseEdgeTexture,
                    thickness,
                }),
            lineSegmentsForMesh: (mesh) => createLineSegmentsForCarcase(mesh),
        });
    };

    const addLabelMeshForSinglePanel = (
        shelfLabel: string,
        parentMesh: THREE.Mesh,
        position: {
            x: number;
            y: number;
            z: number;
        },
        rotation?: number,
        shouldCenterLabel?: boolean
    ) => {
        const fontLoader = new FontLoader();

        fontLoader.load(
            '/templates/3D/fonts/Poppins SemiBold_Regular.json',
            (font) => {
                const textGeometry = new TextGeometry(shelfLabel, {
                    font,
                    size: 4,
                    depth: 0.5,
                });

                const textMesh = new THREE.Mesh(
                    textGeometry,
                    new THREE.MeshStandardMaterial({
                        color: '#333',
                    })
                );

                const scale = 7;
                textMesh.scale.set(scale, scale, scale);

                const boundingBox = new THREE.Box3().setFromObject(textMesh);
                const meshWidth = boundingBox.max.x - boundingBox.min.x;

                if (rotation) textMesh.rotation.z = rotation;

                const box = new THREE.Box3().setFromObject(parentMesh);
                const meshXParent = box.max.x - box.min.x;
                const positionX = box.min.x + meshXParent / 2 - meshWidth / 2;

                textMesh.position.set(
                    shouldCenterLabel ? positionX : position.x,
                    position.y,
                    position.z
                );

                parentMesh.add(textMesh);
            }
        );
    };

    const renderSingleUShapedPanel = () => {
        const radius = 50;

        const width = Number(values.cabinet_panel_width);
        const width1 = Number(values.cabinet_width1);
        const depth = Number(values.cabinet_panel_length);
        const length1 = Number(values.cabinet_length1);
        const length2 = Number(values.cabinet_length2);
        const cutoutWidth = depth - (length1 + length2);
        const cutoutHeight = depth * 0.5;

        const mesh = generateUShapeMesh({
            width: depth,
            depth: width,
            cutoutWidth,
            cutoutHeight,
            radius,
            carcaseTexture,
            carcaseEdgeTexture,
            complexRadii: {
                exteriorRadius: Number(values.cabinet_ext_radius),
                interiorRadius: Number(values.cabinet_int_radius),
            },
            sides: {
                left: length1,
                right: length2,
            },
            customCutoutHeight: width1,
            carcaseThickness,
            horizontalGrainCarc,
            showTexture,
        });

        // Center geometry first so label offsets are in the same shifted coordinate space.
        // geometry.center() moves vertices by (-depth/2, -width/2), so all label x/y
        // positions are adjusted by those same amounts.
        mesh.geometry.center();

        addLabelMeshForSinglePanel(
            'Length',
            mesh,
            {
                x: 0,
                y: width / 2 + 20,
                z: 0,
            },
            undefined,
            true
        );

        addLabelMeshForSinglePanel(
            'Length 1',
            mesh,
            {
                x: length1 / 2 - 80 - depth / 2,
                y: -width / 2 - 40,
                z: 0,
            },
            undefined
        );

        addLabelMeshForSinglePanel(
            'Length 2',
            mesh,
            {
                x: depth / 2 - length2 / 2 - 80,
                y: -width / 2 - 40,
                z: 0,
            },
            undefined
        );

        addLabelMeshForSinglePanel(
            'Width',
            mesh,
            {
                x: -depth / 2 - 20,
                y: -70,
                z: 0,
            },
            (90 * Math.PI) / 180
        );

        addLabelMeshForSinglePanel(
            'Width 1',
            mesh,
            {
                x: length1 - depth / 2 + 40,
                y: width1 / 2 - width / 2 - 70,
                z: 0,
            },
            (90 * Math.PI) / 180
        );

        const lineSegments = createLineSegmentsForCarcase(mesh);
        mesh.add(lineSegments);
        scene.current?.add(mesh);
    };

    const generateCornerShapeMesh = ({
        leftWidth,
        leftDepth,
        rightDepth,
        rightWidth,
        carcaseTexture,
        carcaseEdgeTexture,
        thickness = carcaseThickness,
    }: {
        leftWidth: number;
        leftDepth: number;
        rightDepth: number;
        rightWidth: number;
        carcaseTexture: THREE.Texture;
        carcaseEdgeTexture: THREE.Texture;
        thickness?: number;
    }) => {
        // Create the shape
        const shape = new THREE.Shape();

        // Start at the bottom-left corner
        shape.moveTo(0, 0);

        // Draw the left vertical line
        shape.lineTo(0, leftWidth);

        // Draw the top horizontal line to the right (end of left width)
        shape.lineTo(leftDepth, leftWidth);

        // Draw the remaining horizontal line to the right
        shape.lineTo(rightWidth, rightDepth);

        // Draw the right vertical line upwards
        shape.lineTo(rightWidth, 0);

        // Close the shape by drawing the bottom horizontal line to the left
        shape.lineTo(0, 0);

        const extrudeSettings = {
            steps: 1,
            depth: thickness,
            bevelEnabled: false,
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

        const materials = applyCarcaseMaterial({
            geometry,
            texture: carcaseTexture,
            edgeTexture: carcaseEdgeTexture,
            useSubstrateEdge: false,
            flipGrain: variables?.isClipProduct ? true : false,
            hasHorizontalGrain: variables?.isClipProduct
                ? horizontalGrainExt
                : horizontalGrainCarc,
            showTexture,
            nonSupplyCarcase,
            carcaseSubstrateMaterial,
        });

        // Create the mesh
        return new THREE.Mesh(geometry, materials);
    };

    const generateFfRailOnFlatMesh = ({
        leftWidth,
        leftDepth,
        rightDepth,
        rightWidth,
        carcaseTexture,
        carcaseEdgeTexture,
    }: {
        leftWidth: number;
        leftDepth: number;
        rightDepth: number;
        rightWidth: number;
        carcaseTexture: THREE.Texture;
        carcaseEdgeTexture: THREE.Texture;
    }) => {
        // Create the shape
        const shape = new THREE.Shape();

        const railWidth = 120;

        shape.moveTo(leftDepth - railWidth, carcaseThickness);
        shape.lineTo(leftDepth, carcaseThickness);
        shape.lineTo(rightWidth, leftWidth - rightDepth);
        shape.lineTo(rightWidth, leftWidth - rightDepth + railWidth);
        shape.lineTo(leftDepth - railWidth, carcaseThickness);

        const extrudeSettings = {
            steps: 1,
            depth: carcaseThickness,
            bevelEnabled: false,
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

        const materials = applyCarcaseMaterial({
            geometry,
            texture: carcaseTexture,
            edgeTexture: carcaseEdgeTexture,
            hasHorizontalGrain: horizontalGrainCarc,
            showTexture,
            nonSupplyCarcase,
            carcaseSubstrateMaterial,
        });

        // Create the mesh
        return new THREE.Mesh(geometry, materials);
    };

    const renderCornerShapedPanel = () => {
        const [widthOffset] = getCornerShapeOffsets();
        const rightWidth =
            values.cabinet_right_width - widthOffset + carcaseThickness * 2 + 2;
        const leftWidth = Number(values.cabinet_left_width) + 2;

        const shelvesCount = resolveShapedShelfCount(values);
        const advancedShelves = values.shelves as unknown as AdvancedShelf[];
        return buildCornerShapedPanel({
            values,
            variables,
            shelvesCount,
            advancedShelves,
            carcaseThickness,
            shelfThickness,
            isFfRailOnFlat,
            isFfSolidTop,
            leftWidth,
            rightWidth,
            makeFfRailOnFlatMesh: () =>
                generateFfRailOnFlatMesh({
                    leftWidth,
                    leftDepth: values.cabinet_left_depth - carcaseThickness,
                    rightDepth: Number(values.cabinet_right_depth),
                    rightWidth,
                    carcaseTexture,
                    carcaseEdgeTexture,
                }),
            makeLShapeShelfMesh: ({
                leftDepth,
                rightDepth,
                rightWidth,
                thickness,
            }) =>
                generateLShapeMesh({
                    leftWidth,
                    leftDepth,
                    rightDepth,
                    radius: 50,
                    rightWidth,
                    carcaseTexture,
                    carcaseEdgeTexture,
                    carcaseThickness: thickness,
                    values,
                }),
            makeCornerShelfMesh: ({
                leftDepth,
                rightDepth,
                rightWidth,
                shouldApplyOffset,
                thickness,
            }) =>
                generateCornerShapeMesh({
                    leftWidth,
                    leftDepth: leftDepth - (shouldApplyOffset ? 6 : 1),
                    rightDepth,
                    rightWidth,
                    carcaseTexture,
                    carcaseEdgeTexture,
                    thickness,
                }),
            lineSegmentsForMesh: (mesh) => createLineSegmentsForCarcase(mesh),
            onLabel: (mesh, ctx) => {
                const selectedShelf = advancedShelves?.[ctx.index - 1];
                const shelfLabel = `Shelf ${ctx.index} - ${getShelfStyleLabel(
                    values,
                    selectedShelf?.shelf_style
                )}`;
                addLabelMesh(
                    shelfLabel,
                    ctx.panelThickness ?? carcaseThickness,
                    mesh,
                    ctx.suggestedLabelY ?? 0,
                    ctx.suggestedLabelXOffset ?? 0,
                    ctx.suggestedLabelRotationY ?? 0,
                    ctx.suggestedLabelX
                );
            },
        });
    };

    const renderCornerShapePanelForPantry = () => {
        const [widthOffset] = getCornerShapeOffsets();
        const [widthOffsetLShape] = getCornerLShapeOffsets();

        const upperShelvesCount = values?.upper_shelves
            ? (values?.upper_shelves as unknown as Array<number>)?.length
            : 0;
        const lowerShelvesCount = values?.lower_shelves
            ? (values?.lower_shelves as unknown as Array<number>)?.length
            : 0;

        const upperShelves =
            values.upper_shelves as unknown as AdvancedUpperShelf[];
        const lowerShelves =
            values.lower_shelves as unknown as AdvancedLowerShelf[];
        const shelvesHeightReference = {
            upper_shelves:
                Number(values.cabinet_height) -
                Number(values.cabinet_partition_height),
            lower_shelves: Number(values.cabinet_partition_height),
        };

        const upperShelfOffset = shelvesHeightReference.lower_shelves;
        const height = Number(values.cabinet_height) - upperShelfOffset;

        const rightWidth = values.cabinet_right_width - widthOffset;
        const leftWidth = Number(values.cabinet_left_width) + 2;

        return buildCornerShapePanelForPantry({
            values,
            variables,
            carcaseThickness,
            shelfThickness,
            leftWidth,
            rightWidth,
            widthOffsetLShape,
            upperShelves,
            lowerShelves,
            upperShelvesCount,
            lowerShelvesCount,
            upperShelfOffset,
            height,
            lowerStartIndex: variables?.is2Part ? 0 : 1,
            lowerEndExtra: variables?.noCornerBaseBottom ? 0 : 1,
            makeLShapeMesh: ({leftDepth, rightDepth, rightWidth, thickness}) =>
                generateLShapeMesh({
                    leftWidth,
                    leftDepth,
                    rightDepth,
                    radius: 50,
                    rightWidth,
                    carcaseTexture,
                    carcaseEdgeTexture,
                    carcaseThickness: thickness,
                    values,
                }),
            makeCornerShapeMesh: ({
                leftDepth,
                rightDepth,
                rightWidth,
                thickness,
            }) =>
                generateCornerShapeMesh({
                    leftWidth,
                    leftDepth,
                    rightDepth,
                    rightWidth: rightWidth + carcaseThickness * 2 + 2,
                    carcaseTexture,
                    carcaseEdgeTexture,
                    thickness,
                }),
            lineSegmentsForMesh: (mesh) => createLineSegmentsForCarcase(mesh),
            onLabel: (mesh, ctx) => {
                if (ctx.label === 'Horizontal Partition') {
                    addLabelMesh(
                        'Horizontal Partition',
                        ctx.panelThickness ?? carcaseThickness,
                        mesh,
                        ctx.suggestedLabelY ?? 0,
                        ctx.suggestedLabelXOffset ?? 0
                    );
                    return;
                }

                const prefix = ctx.label.startsWith('Shelf ')
                    ? ctx.label.replace(
                          'Shelf',
                          ctx.index <= upperShelvesCount + 1
                              ? 'Upper Shelf'
                              : 'Lower Shelf'
                      )
                    : ctx.label;
                const shelfLabel = `${prefix} - ${getShelfStyleLabel(
                    values,
                    ctx.shelfStyle
                )}`;

                addLabelMesh(
                    shelfLabel,
                    ctx.panelThickness ?? carcaseThickness,
                    mesh,
                    ctx.suggestedLabelY ?? 0,
                    ctx.suggestedLabelXOffset ?? 0
                );
            },
        });
    };

    const renderCornerShapePanelFor3PartPantry = () => {
        const [widthOffset] = getCornerShapeOffsets();
        const [widthOffsetLShape] = getCornerLShapeOffsets();

        const upperShelvesCount = values?.upper_shelves
            ? (values?.upper_shelves as unknown as Array<number>)?.length
            : 0;
        const middleShelvesCount = values?.middle_shelves
            ? (values?.middle_shelves as unknown as Array<number>)?.length
            : 0;
        const lowerShelvesCount = values?.lower_shelves
            ? (values?.lower_shelves as unknown as Array<number>)?.length
            : 0;

        const upperShelves =
            values.upper_shelves as unknown as AdvancedUpperShelf[];
        const middleShelves =
            values.middle_shelves as unknown as AdvancedMiddleShelf[];
        const lowerShelves =
            values.lower_shelves as unknown as AdvancedLowerShelf[];

        const cabinetUpperPartitionHeight =
            Number(values.cabinet_upper_partition_height) > 1
                ? Number(values.cabinet_upper_partition_height) /
                  Number(values.cabinet_height)
                : Number(values.cabinet_upper_partition_height);
        const cabinetLowerPartitionHeight =
            Number(values.cabinet_lower_partition_height) > 1
                ? Number(values.cabinet_lower_partition_height) /
                  Number(values.cabinet_height)
                : Number(values.cabinet_lower_partition_height);

        const upperShelfOffset =
            cabinetUpperPartitionHeight * Number(values.cabinet_height);
        const lowerShelfOffset =
            cabinetLowerPartitionHeight * Number(values.cabinet_height);

        const heightUpper = Number(values.cabinet_height) - upperShelfOffset;
        const heightMiddle = upperShelfOffset - lowerShelfOffset;
        const heightLower = lowerShelfOffset;

        const rightWidth = values.cabinet_right_width - widthOffset;
        const leftWidth = Number(values.cabinet_left_width) + 2;
        return buildCornerShapePanelFor3PartPantry({
            values,
            variables,
            carcaseThickness,
            shelfThickness,
            leftWidth,
            rightWidth,
            widthOffsetLShape,
            upperShelves,
            middleShelves,
            lowerShelves,
            upperShelvesCount,
            middleShelvesCount,
            lowerShelvesCount,
            upperShelfOffset,
            lowerShelfOffset,
            heightUpper,
            heightMiddle,
            heightLower,
            lowerEndExtra: variables?.noCornerBaseBottom ? 0 : 1,
            makeLShapeMesh: ({leftDepth, rightDepth, rightWidth, thickness}) =>
                generateLShapeMesh({
                    leftWidth,
                    leftDepth,
                    rightDepth,
                    radius: 50,
                    rightWidth,
                    carcaseTexture,
                    carcaseEdgeTexture,
                    carcaseThickness: thickness,
                    values,
                }),
            makeCornerShapeMesh: ({
                leftDepth,
                rightDepth,
                rightWidth,
                thickness,
            }) =>
                generateCornerShapeMesh({
                    leftWidth,
                    leftDepth,
                    rightDepth,
                    rightWidth: rightWidth + carcaseThickness * 2 + 2,
                    carcaseTexture,
                    carcaseEdgeTexture,
                    thickness,
                }),
            lineSegmentsForMesh: (mesh) => createLineSegmentsForCarcase(mesh),
            onLabel: (mesh, ctx) => {
                if (
                    ctx.label === 'Upper Partition' ||
                    ctx.label === 'Lower Partition'
                ) {
                    addLabelMesh(
                        ctx.label,
                        ctx.panelThickness ?? carcaseThickness,
                        mesh,
                        ctx.suggestedLabelY ?? 0,
                        ctx.suggestedLabelXOffset ?? 0
                    );
                    return;
                }

                const shelfLabel = `${ctx.label} - ${getShelfStyleLabel(
                    values,
                    ctx.shelfStyle
                )}`;
                addLabelMesh(
                    shelfLabel,
                    ctx.panelThickness ?? carcaseThickness,
                    mesh,
                    ctx.suggestedLabelY ?? 0,
                    ctx.suggestedLabelXOffset ?? 0
                );
            },
        });
    };

    const generateRadiusShapeMesh = ({
        width,
        depth,
        texture,
        edgeTexture,
    }: {
        width: number;
        depth: number;
        texture: THREE.Texture;
        edgeTexture: THREE.Texture;
    }) => {
        const shape = new THREE.Shape();

        applyQuarterCircleArcShape(shape, width, depth);

        const extrudeSettings = {
            steps: 1,
            depth: exteriorThickness,
            bevelEnabled: false,
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

        const materials = applyCarcaseMaterial({
            geometry,
            texture,
            edgeTexture,
            useSubstrateEdge: false,
            flipGrain: !variables?.isLeftRadius,
            hasHorizontalGrain: horizontalGrainExt,
            showTexture,
            nonSupplyCarcase,
            carcaseSubstrateMaterial,
        });

        // Create the mesh
        return new THREE.Mesh(geometry, materials);
    };

    const renderClipPanel = () => {
        const shelvesCount = Number(values.simple_shelves_amount);
        const advancedShelves = values.shelves as unknown as AdvancedShelf[];

        return buildClipPanel({
            values,
            variables,
            shelvesCount,
            advancedShelves,
            exteriorThickness,
            makeCornerShapeMesh: ({
                leftWidth,
                leftDepth,
                rightDepth,
                rightWidth,
            }) =>
                generateCornerShapeMesh({
                    leftWidth,
                    leftDepth,
                    rightDepth,
                    rightWidth,
                    carcaseTexture: exteriorTexture,
                    carcaseEdgeTexture: exteriorEdgeTexture,
                    thickness: exteriorThickness,
                }),
            lineSegmentsForMesh: (mesh) => createLineSegmentsForCarcase(mesh),
            onLabel: (mesh, ctx) => {
                const selectedShelf = advancedShelves?.[ctx.index - 1];
                const shelfLabel = `Shelf ${ctx.index} - ${getShelfStyleLabel(
                    values,
                    selectedShelf?.shelf_style
                )}`;

                addLabelMesh(
                    shelfLabel,
                    exteriorThickness,
                    mesh,
                    ctx.suggestedLabelY ?? 0,
                    ctx.suggestedLabelXOffset ?? 0,
                    ctx.suggestedLabelRotationY ?? 0
                );
            },
        });
    };

    const renderRadiusPanel = () => {
        const shelvesCount = Number(values.simple_shelves_amount);
        const advancedShelves = values.shelves as unknown as AdvancedShelf[];
        const depth = Number(values.cabinet_depth) - exteriorThickness;
        const width = Number(values.cabinet_width) - exteriorThickness;

        return buildRadiusPanel({
            values,
            variables,
            shelvesCount,
            advancedShelves,
            exteriorThickness,
            makeRadiusMesh: ({offset}) =>
                generateRadiusShapeMesh({
                    width: (!variables?.isLeftRadius ? depth : width) - offset,
                    depth: (!variables?.isLeftRadius ? width : depth) - offset,
                    texture: exteriorTexture,
                    edgeTexture: exteriorEdgeTexture,
                }),
            lineSegmentsForMesh: (mesh) => createLineSegmentsForCarcase(mesh),
            onLabel: (mesh, ctx) => {
                const selectedShelf = advancedShelves?.[ctx.index - 1];
                const shelfLabel = `Shelf ${ctx.index} - ${getShelfStyleLabel(
                    values,
                    selectedShelf?.shelf_style
                )}`;
                addLabelMesh(shelfLabel, exteriorThickness, mesh, depth / 2);
            },
        });
    };

    const renderLShapedPanelForPantry = () => {
        const upperShelvesCount = values?.upper_shelves
            ? (values?.upper_shelves as unknown as Array<number>)?.length
            : 0;
        const lowerShelvesCount = values?.lower_shelves
            ? (values?.lower_shelves as unknown as Array<number>)?.length
            : 0;

        const upperShelves =
            values.upper_shelves as unknown as AdvancedUpperShelf[];
        const lowerShelves =
            values.lower_shelves as unknown as AdvancedLowerShelf[];
        const shelvesHeightReference = {
            upper_shelves:
                parseFloat(values.cabinet_height?.toString()) -
                values.cabinet_partition_height,
            lower_shelves: values.cabinet_partition_height,
        };

        const upperShelfOffset = shelvesHeightReference.lower_shelves;
        const height = Number(values.cabinet_height) - upperShelfOffset;

        const rightWidth = values.cabinet_right_width - carcaseThickness;
        const leftWidth = Number(values.cabinet_left_width) + 2;

        return buildLShapedPanelForPantry({
            values,
            variables,
            carcaseThickness,
            shelfThickness,
            upperShelves,
            lowerShelves,
            upperShelvesCount,
            lowerShelvesCount,
            upperShelfOffset,
            height,
            leftWidth,
            rightWidth,
            radius: 20,
            makeLShapeMesh: ({leftDepth, rightDepth, thickness}) =>
                generateLShapeMesh({
                    leftWidth,
                    leftDepth,
                    rightDepth,
                    radius: 20,
                    rightWidth,
                    carcaseTexture,
                    carcaseEdgeTexture,
                    carcaseThickness: thickness,
                    values,
                }),
            lineSegmentsForMesh: (mesh) => createLineSegmentsForCarcase(mesh),
            onLabel: (mesh, ctx) => {
                addLabelMesh(
                    `${ctx.label} - ${getShelfStyleLabel(values, ctx.shelfStyle)}`,
                    ctx.panelThickness ?? carcaseThickness,
                    mesh,
                    ctx.suggestedLabelY ?? 0,
                    ctx.suggestedLabelXOffset ?? 0
                );
            },
        });
    };

    const generateRangehoodCutoutShapeMesh = ({
        width,
        depth,
        carcaseTexture,
        carcaseEdgeTexture,
        thickness = carcaseThickness,
    }: {
        width: number;
        depth: number;
        carcaseTexture: THREE.Texture;
        carcaseEdgeTexture: THREE.Texture;
        thickness?: number;
    }) => {
        const ventRightPositionX =
            width / 2 + DEFAULT_VENT_FRONT_WIDTH / 2 - carcaseThickness / 2;
        const ventLeftPositionX =
            width / 2 - DEFAULT_VENT_FRONT_WIDTH / 2 - carcaseThickness / 2;
        const ventFrontPositionY =
            depth - DEFAULT_VENT_WIDTH - carcaseThickness;
        // Create the shape
        const shape = new THREE.Shape();

        shape.moveTo(0, 0);

        shape.lineTo(width, 0);
        shape.lineTo(width, depth);
        shape.lineTo(ventRightPositionX, depth);
        shape.lineTo(ventRightPositionX, ventFrontPositionY);
        shape.lineTo(ventLeftPositionX, ventFrontPositionY);
        shape.lineTo(ventLeftPositionX, depth);
        shape.lineTo(0, depth);

        // Close the shape by drawing the bottom horizontal line to the left
        shape.lineTo(0, 0);

        const extrudeSettings = {
            steps: 1,
            depth: thickness,
            bevelEnabled: false,
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

        const materials = applyCarcaseMaterial({
            geometry,
            texture: carcaseTexture,
            edgeTexture: carcaseEdgeTexture,
            useSubstrateEdge: false,
            flipGrain: true,
            hasHorizontalGrain: horizontalGrainCarc,
            showTexture,
            nonSupplyCarcase,
            carcaseSubstrateMaterial,
        });

        // Create the mesh
        return new THREE.Mesh(geometry, materials);
    };

    const renderShelvesWithRangehoodCutout = () => {
        const shelvesCount = Number(values.simple_shelves_amount);
        const advancedShelves = values.shelves as unknown as AdvancedShelf[];
        return buildShelvesWithRangehoodCutout({
            values,
            carcaseThickness,
            shelfThickness,
            shelvesCount,
            advancedShelves,
            makeRangehoodCutoutMesh: ({width, depth, thickness}) =>
                generateRangehoodCutoutShapeMesh({
                    width,
                    depth,
                    carcaseTexture,
                    carcaseEdgeTexture,
                    thickness,
                }),
            lineSegmentsForMesh: (mesh) => createLineSegmentsForCarcase(mesh),
            onLabel: (mesh, ctx) => {
                const selectedShelf = advancedShelves?.[ctx.index - 1];
                const shelfLabel = `Shelf ${ctx.index} - ${getShelfStyleLabel(
                    values,
                    selectedShelf?.shelf_style
                )}`;
                addLabelMesh(
                    shelfLabel,
                    ctx.panelThickness ?? carcaseThickness,
                    mesh,
                    20
                );
            },
        });
    };

    const renderSingleKeyShapePanel = () => {
        const leftWidth = Number(values.cabinet_panel_width);
        const rightWidth = Number(values.cabinet_panel_length);
        const leftDepth = Number(values.cabinet_width_depth);
        const rightDepth = Number(values.cabinet_length_depth);

        const mesh = generateKeyShapeMesh({
            leftWidth,
            leftDepth,
            rightDepth,
            rightWidth,
            carcaseTexture,
            carcaseEdgeTexture,
            carcaseThickness,
            horizontalGrainCarc,
            showTexture,
        });

        // Mirror the L-shape panel pattern (renderSingleLShapePanel):
        // center the geometry around the origin, then rotateAroundCenter by
        // PI to land in the same orientation the camera already fits. Avoids
        // the prior manual rotation.x=90° / scale.y=-1 / position offsets
        // that stood the panel vertical instead of laying it flat.
        mesh.geometry.center();
        rotateAroundCenter(mesh, Math.PI);

        const lineSegments = createLineSegmentsForCarcase(mesh);

        mesh.add(lineSegments);
        scene.current.add(mesh);
    };

    const renderBottomPanelWithVent = () => {
        const width = Number(values.cabinet_width) - carcaseThickness;
        const depth = Number(values.cabinet_depth) - carcaseThickness;
        const cutoutWidth = variables.cutoutWidth;
        const inset = variables.inset;
        return buildBottomPanelWithVent({
            values,
            carcaseThickness,
            width,
            depth,
            cutoutWidth,
            inset,
            makeBottomPanelMesh: ({width, depth, cutoutWidth, inset}) =>
                generateBottomPanelWithVentMesh({
                    width,
                    depth,
                    cutoutWidth,
                    inset,
                    carcaseTexture,
                    carcaseEdgeTexture,
                }),
            lineSegmentsForMesh: (mesh) => createLineSegmentsForCarcase(mesh),
        });
    };

    const generateBottomPanelWithVentMesh = ({
        width,
        depth,
        cutoutWidth,
        inset,
        carcaseTexture,
        carcaseEdgeTexture,
    }: {
        width: number;
        depth: number;
        cutoutWidth: number;
        inset: number;
        carcaseTexture: THREE.Texture;
        carcaseEdgeTexture: THREE.Texture;
    }) => {
        const shape = new THREE.Shape();

        shape.moveTo(0, 0);
        shape.lineTo(0, depth - inset); // left
        shape.lineTo(width, depth - inset); // front
        shape.lineTo(width, 0); // right
        shape.lineTo(width - cutoutWidth, 0); // back to right x offset
        shape.lineTo(width - cutoutWidth, cutoutWidth); // back to right y offset
        shape.lineTo(cutoutWidth, cutoutWidth); // back to left y offset
        shape.lineTo(cutoutWidth, 0); // back to left x offset
        shape.lineTo(0, 0); // close shape

        const extrudeSettings = {
            steps: 1,
            depth: carcaseThickness,
            bevelEnabled: false,
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        const materials = applyCarcaseMaterial({
            geometry,
            texture: carcaseTexture,
            edgeTexture: carcaseEdgeTexture,
            useSubstrateEdge: false,
            flipGrain: true,
            hasHorizontalGrain: horizontalGrainCarc,
            showTexture,
            nonSupplyCarcase,
            carcaseSubstrateMaterial,
        });

        return new THREE.Mesh(geometry, materials);
    };

    const renderTopBottomLShapedPanels = () => {
        const height = Number(values.cabinet_height);
        const isUpperProduct = Boolean(variables?.isUpperProduct);
        const radius = 20;

        const rightWidth =
            Number(values.cabinet_right_width) -
            carcaseThickness -
            carcaseThickness;
        const leftWidth =
            Number(values.cabinet_left_width) +
            2 +
            (isUpperProduct
                ? -(carcaseThickness * 2) - 2
                : -(carcaseThickness * 2));
        const rightDepth =
            Number(values.cabinet_right_depth) -
            carcaseThickness -
            1 -
            carcaseThickness;
        const leftDepth =
            Number(values.cabinet_left_depth) - carcaseThickness - 1 + 1;

        const isSolidTop = values.cabinet_top == CabinetTopEnum.SOLID_TOP;
        const panelCount = isSolidTop ? 2 : 1;
        return buildTopBottomLShapedPanels({
            values,
            variables,
            carcaseThickness,
            height,
            panelCount,
            leftWidth,
            rightWidth,
            leftDepth,
            rightDepth,
            radius,
            makeLShapeMesh: ({
                leftDepth,
                rightDepth,
                leftWidth,
                rightWidth,
                radius,
            }) =>
                generateLShapeMesh({
                    leftWidth,
                    leftDepth,
                    rightDepth,
                    radius,
                    rightWidth,
                    carcaseTexture,
                    carcaseEdgeTexture,
                    carcaseThickness,
                    values,
                }),
            lineSegmentsForMesh: (mesh) => createLineSegmentsForCarcase(mesh),
        });
    };

    const renderArrowShapePanelForLSDrawers = () => {
        const leftWidth = Number(values.cabinet_left_width);
        const rightWidth = Number(values.cabinet_right_width);
        const leftDepth = Number(values.cabinet_left_depth);
        const rightDepth = Number(values.cabinet_right_depth);
        const data = generateDrawers(
            values.drawers,
            getDrawerFormValues(values),
            variables,
            {
                ...values,
                cabinet_carc_thickness: carcaseThickness,
                cabinet_ext_thickness: exteriorThickness,
            }
        );
        const meshes: Array<
            THREE.Mesh<THREE.BufferGeometry, THREE.Material | THREE.Material[]>
        > = [];
        data?.drawers.forEach((drawer, index) => {
            const mesh = generateArrowShapeMesh({
                leftWidth,
                leftDepth,
                rightDepth,
                rightWidth,
                carcaseTexture,
                carcaseEdgeTexture,
                carcaseThickness,
                horizontalGrainCarc,
                showTexture,
            }) as unknown as THREE.Mesh<
                THREE.BufferGeometry,
                THREE.Material | THREE.Material[]
            >;

            mesh.userData.isCornerDrawerPart = true;
            mesh.userData.index = index;

            mesh.position.set(
                carcaseThickness + 25,
                leftWidth - 25,
                drawer.bottomPosition
            );

            mesh.scale.y = -1;

            const lineSegments = createLineSegmentsForCarcase(mesh);

            mesh.add(lineSegments);
            meshes.push(mesh);
        });
        return meshes;
    };

    const shapedParts: {
        condition: boolean;
        callback: () => THREE.Mesh[] | void;
    }[] = [
        {
            condition: variables.hasLShapedShelves,
            callback: renderLShapedPanel,
        },
        {
            condition: !(
                typeof values.lower_shelves !== 'undefined' ||
                typeof values.upper_shelves !== 'undefined'
            ),
            callback: renderUShapedPanel,
        },
        {
            condition: variables.isReturnProduct,
            callback: renderLShapedPanelForReturn,
        },
        {
            condition: !(
                !values?.upper_shelves ||
                values?.shelves ||
                variables?.isReturnProduct
            ),
            callback: renderUpperAndLowerUShapeShelves,
        },
        {
            condition: Boolean(values?.upper_shelves && values?.shelves),
            callback: renderUpperAndLowerLeftRightUShapedShelves,
        },
        {
            condition: variables.isLShapePanel,
            callback: renderSingleLShapePanel,
        },
        {
            condition:
                variables.isReturnProduct && variables?.shelfType === 'PANTRY',
            callback: renderLShapedPanelForPantryReturn,
        },
        {
            condition: Boolean(values.oven_opening_height),
            callback: renderShelvesWithOvenVents,
        },
        {
            condition: variables.isUShapedPanel,
            callback: renderSingleUShapedPanel,
        },
        {
            condition:
                variables.hasCornerShapedShelves && !values?.upper_shelves,
            callback: renderCornerShapedPanel,
        },
        {
            condition:
                !(
                    !values?.upper_shelves ||
                    values?.shelves ||
                    variables?.isReturnProduct
                ) &&
                variables?.isCornerProduct &&
                !Boolean(values.middle_shelves),
            callback: renderCornerShapePanelForPantry,
        },
        {
            condition:
                !(
                    !values?.upper_shelves ||
                    values?.shelves ||
                    variables?.isReturnProduct
                ) &&
                variables?.isCornerProduct &&
                Boolean(values.middle_shelves),
            callback: renderCornerShapePanelFor3PartPantry,
        },
        {
            condition: variables.isClipProduct,
            callback: renderClipPanel,
        },
        {
            condition: variables.isRadiusProduct,
            callback: renderRadiusPanel,
        },
        {
            condition: variables.isLShapedPantry,
            callback: renderLShapedPanelForPantry,
        },
        {
            condition: variables.hasRangehoodVent,
            callback: renderShelvesWithRangehoodCutout,
        },
        {
            condition: variables.isKeyShapePanel,
            callback: renderSingleKeyShapePanel,
        },
        {
            condition: variables.hasBottomVent,
            callback: renderBottomPanelWithVent,
        },
        {
            condition: variables.isLShapeDrawerProduct,
            callback: renderTopBottomLShapedPanels,
        },
        {
            condition: variables.isLShapeDrawerProduct,
            callback: renderArrowShapePanelForLSDrawers,
        },
    ];

    return {
        shapedParts,
    };
};

export default useShapedPanel;
